import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MessageSquare, Store, Receipt, Menu, X, LogOut, LogIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CartDrawer } from "./cart-drawer";
import { CountrySelector } from "./country-selector";
import { useAuth } from "@/lib/auth-context";

export function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isAuthenticated, isCustomer, isRestaurant, loading, logout } = useAuth();

  const navItems = [
    { href: "/", label: "Chat", icon: MessageSquare },
    { href: "/restaurants", label: "Restaurants", icon: Store },
    { href: "/orders", label: "Orders", icon: Receipt },
  ];

  const displayName = user?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "Account";

  const handleAuthClick = () => {
    if (isAuthenticated) {
      logout();
      navigate("/");
    } else {
      navigate("/signin");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-card border-b sticky top-0 z-40">
        <Link to="/" className="font-serif font-bold text-2xl text-primary tracking-tight" data-testid="nav-logo">
          Cravve
        </Link>
        <div className="flex items-center gap-2">
          <CountrySelector />
          <CartDrawer />
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[73px] bg-background z-30 p-4 border-t flex flex-col gap-2 overflow-y-auto">
          {navItems.map((it) => (
            <Link
              key={it.href}
              to={it.href}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${it.label.toLowerCase()}`}
              className={`flex items-center gap-3 p-4 rounded-lg text-lg font-medium transition-colors ${
                location.pathname === it.href
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <it.icon size={22} />
              {it.label}
            </Link>
          ))}
          <div className="mt-auto pt-4 border-t">
            {!loading && (
              isAuthenticated ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                      {displayName[0]?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium truncate">{displayName}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleAuthClick} className="text-muted-foreground gap-1.5" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </Button>
                </div>
              ) : (
                <Button className="w-full gap-2" onClick={handleAuthClick} data-testid="signin-btn">
                  <LogIn className="w-4 h-4" />
                  Sign in
                </Button>
              )
            )}
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-card border-r fixed h-screen top-0 left-0 z-10">
        <div className="p-6">
          <Link to="/" className="font-serif font-bold text-3xl text-primary tracking-tight" data-testid="nav-logo-desktop">
            Cravve
          </Link>
        </div>
        <nav className="flex-1 px-4 flex flex-col gap-2">
          {navItems.map((it) => (
            <Link
              key={it.href}
              to={it.href}
              data-testid={`nav-desktop-${it.label.toLowerCase()}`}
              className={`flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
                location.pathname === it.href
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <it.icon size={20} />
              {it.label}
            </Link>
          ))}
        </nav>

        <div className="px-4 pb-3 space-y-3">
          <CountrySelector />
          {!loading && (
            isAuthenticated ? (
              <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors group">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary text-sm font-semibold">
                  {displayName[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{displayName}</p>
                  {isRestaurant && <p className="text-[10px] text-primary uppercase tracking-wide font-semibold">Restaurant</p>}
                </div>
                <button
                  onClick={handleAuthClick}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted-foreground/10"
                  title="Sign out"
                  data-testid="logout-btn-desktop"
                >
                  <LogOut className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            ) : (
              <Button variant="outline" className="w-full gap-2 text-sm" onClick={handleAuthClick} data-testid="signin-btn-desktop">
                <LogIn className="w-4 h-4" />
                Sign in
              </Button>
            )
          )}
        </div>

        <div className="p-4 pt-0 border-t">
          <CartDrawer fullWidth />
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 flex flex-col relative w-full pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t flex justify-around p-2 z-40">
        {navItems.map((it) => (
          <Link
            key={it.href}
            to={it.href}
            data-testid={`bottom-nav-${it.label.toLowerCase()}`}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg ${
              location.pathname === it.href ? "text-primary" : "text-muted-foreground"
            }`}
          >
            <it.icon size={20} />
            <span className="text-[10px] font-medium">{it.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
